import { useState, useEffect } from "react";

// @mui
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

// MD components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Services
import WorkspaceService from "services/workspace-service";

// Role descriptions sourced from workspace role seed
const ROLE_DESCRIPTIONS = {
    Admin: "Full workspace management, excluding billing.",
    Manager: "Operational lead. Can manage profiles, snapshots and reports.",
    Analyst: "Data insight specialist. Can view all data and generate reports.",
    Viewer: "Read-only access to stats and dashboards.",
};

/**
 * ManageTeamModal — reusable Dialog version of the Manage Team drawer.
 * Used as the invite-members onboarding step after first profile connection.
 *
 * Props:
 *   open          — boolean
 *   onClose       — () => void  — called on skip / done / X
 *   workspaceId   — string      — active workspace id
 *   workspaceName — string      — display name
 *   isOnboarding  — bool        — if true, shows "Skip for now" and a welcoming header
 */
function ManageTeamModal({ open, onClose, workspaceId, workspaceName, isOnboarding = false }) {
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteRole, setInviteRole] = useState("");
    const [inviting, setInviting] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    const [members, setMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);

    const [pendingInvitations, setPendingInvitations] = useState([]);
    const [loadingInvitations, setLoadingInvitations] = useState(false);

    const [memberSearch, setMemberSearch] = useState("");
    const [invitationSearch, setInvitationSearch] = useState("");

    // Load members & invitations when dialog opens
    useEffect(() => {
        if (open && workspaceId) {
            loadMembers();
            loadInvitations();
        }
    }, [open, workspaceId]);

    const loadMembers = async () => {
        try {
            setLoadingMembers(true);
            const res = await WorkspaceService.fetchWorkspaceMembers(workspaceId);
            setMembers(res || []);
        } catch (err) {
            console.error("Failed to load members", err);
        } finally {
            setLoadingMembers(false);
        }
    };

    const loadInvitations = async () => {
        try {
            setLoadingInvitations(true);
            const res = await WorkspaceService.fetchInvitations(workspaceId);
            setPendingInvitations(res || []);
        } catch (err) {
            console.error("Failed to load invitations", err);
        } finally {
            setLoadingInvitations(false);
        }
    };

    const handleInvite = async () => {
        if (!inviteEmail) {
            setMessage({ type: "error", text: "Please enter an email address." });
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(inviteEmail)) {
            setMessage({ type: "error", text: "Please enter a valid email address." });
            return;
        }
        try {
            setInviting(true);
            setMessage({ type: "", text: "" });
            await WorkspaceService.sendInvitation(workspaceId, inviteEmail, inviteRole);
            setMessage({ type: "success", text: `Invitation sent to ${inviteEmail}!` });
            setInviteEmail("");
            loadInvitations();
        } catch (error) {
            setMessage({
                type: "error",
                text: error.response?.data?.message || "Failed to send invitation.",
            });
        } finally {
            setInviting(false);
        }
    };

    const filteredMembers = members.filter((m) =>
        (m.user_id?.name || "").toLowerCase().includes(memberSearch.toLowerCase()) ||
        (m.user_id?.email || "").toLowerCase().includes(memberSearch.toLowerCase())
    );

    const filteredInvitations = pendingInvitations.filter((inv) =>
        (inv.email || "").toLowerCase().includes(invitationSearch.toLowerCase())
    );

    const roleColor = (role) => {
        if (role === "Owner") return { bgcolor: "#4db6ac", color: "#fff" };
        if (role === "Admin") return { bgcolor: "#1976d2", color: "#fff" };
        return { bgcolor: "#f0f2f5", color: "#344767" };
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={isOnboarding}
            PaperProps={{ sx: { borderRadius: "16px" } }}
        >
            {/* Header */}
            <DialogTitle sx={{ pb: 0 }}>
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                    <MDBox>
                        {isOnboarding && (
                            <MDTypography variant="caption" color="info" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                                🎉 Your workspace is ready!
                            </MDTypography>
                        )}
                        <MDTypography variant="h5" fontWeight="bold">
                            Manage Team
                        </MDTypography>
                        <MDTypography variant="button" color="text">
                            Workspace: {workspaceName}
                        </MDTypography>
                    </MDBox>
                    {!isOnboarding && (
                        <IconButton onClick={onClose} size="small">
                            <Icon>close</Icon>
                        </IconButton>
                    )}
                </MDBox>
            </DialogTitle>

            <Divider sx={{ mt: 1, mb: 0 }} />

            <DialogContent>
                {/* Invite form */}
                <MDTypography variant="subtitle2" fontWeight="bold" mb={1} mt={1}>
                    Invite New Member
                </MDTypography>

                <MDBox mb={2}>
                    <TextField
                        fullWidth
                        label="Email Address"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="colleague@example.com"
                        onKeyDown={(e) => e.key === "Enter" && handleInvite()}
                    />
                </MDBox>

                <MDBox mb={2}>
                    <FormControl fullWidth>
                        <InputLabel id="modal-role-label" shrink>Role</InputLabel>
                        <Select
                            labelId="modal-role-label"
                            displayEmpty
                            notched
                            value={inviteRole}
                            label="Role"
                            onChange={(e) => setInviteRole(e.target.value)}
                            renderValue={(val) =>
                                val ? val : <span style={{ color: "rgba(0,0,0,0.38)" }}>Select a role…</span>
                            }
                            sx={{ height: "45px" }}
                        >
                            <MenuItem value="Admin">
                                <Tooltip title={ROLE_DESCRIPTIONS.Admin} placement="right" arrow><span style={{ display: "block", width: "100%" }}>Admin</span></Tooltip>
                            </MenuItem>
                            <MenuItem value="Manager">
                                <Tooltip title={ROLE_DESCRIPTIONS.Manager} placement="right" arrow><span style={{ display: "block", width: "100%" }}>Manager</span></Tooltip>
                            </MenuItem>
                            <MenuItem value="Analyst">
                                <Tooltip title={ROLE_DESCRIPTIONS.Analyst} placement="right" arrow><span style={{ display: "block", width: "100%" }}>Analyst</span></Tooltip>
                            </MenuItem>
                            <MenuItem value="Viewer">
                                <Tooltip title={ROLE_DESCRIPTIONS.Viewer} placement="right" arrow><span style={{ display: "block", width: "100%" }}>Viewer</span></Tooltip>
                            </MenuItem>
                        </Select>
                    </FormControl>
                </MDBox>

                <MDButton
                    variant="gradient"
                    color="accent"
                    fullWidth
                    onClick={handleInvite}
                    disabled={inviting}
                    startIcon={inviting ? <CircularProgress size={18} color="inherit" /> : <Icon>send</Icon>}
                >
                    {inviting ? "Sending..." : "Send Invitation"}
                </MDButton>

                {message.text && (
                    <MDBox mt={1.5}>
                        <Alert severity={message.type} onClose={() => setMessage({ type: "", text: "" })} sx={{ fontSize: "0.875rem" }}>
                            {message.text}
                        </Alert>
                    </MDBox>
                )}

                {/* Current Members */}
                <MDBox mt={3} p={2} bgcolor="#f8f9fa" borderRadius="lg">
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                        <MDTypography variant="subtitle2" fontWeight="bold">
                            Current Members
                        </MDTypography>
                        <MDBox width="140px">
                            <TextField
                                placeholder="Search..."
                                size="small"
                                fullWidth
                                value={memberSearch}
                                onChange={(e) => setMemberSearch(e.target.value)}
                                InputProps={{ sx: { fontSize: "0.8rem" } }}
                            />
                        </MDBox>
                    </MDBox>

                    {loadingMembers ? (
                        <MDBox display="flex" justifyContent="center" py={2}>
                            <CircularProgress size={20} />
                        </MDBox>
                    ) : filteredMembers.length === 0 ? (
                        <MDTypography variant="caption" color="text" sx={{ fontStyle: "italic" }}>
                            No members found.
                        </MDTypography>
                    ) : (
                        filteredMembers.map((m) => (
                            <MDBox
                                key={m._id}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                p={1.5}
                                mb={1}
                                bgcolor="white"
                                borderRadius="8px"
                                sx={{ border: "1px solid #eee" }}
                            >
                                <MDBox>
                                    <MDTypography variant="button" fontWeight="bold" display="block">
                                        {m.user_id?.name || "Unknown"}
                                    </MDTypography>
                                    <MDTypography variant="caption" color="text">
                                        {m.user_id?.email}
                                    </MDTypography>
                                </MDBox>
                                <Chip
                                    label={m.role}
                                    size="small"
                                    sx={{ ...roleColor(m.role), fontWeight: "bold", fontSize: "0.7rem" }}
                                />
                            </MDBox>
                        ))
                    )}
                </MDBox>

                {/* Pending Invitations */}
                <MDBox mt={2} p={2} bgcolor="#f8f9fa" borderRadius="lg">
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                        <MDTypography variant="subtitle2" fontWeight="bold">
                            Pending Invitations
                        </MDTypography>
                        <MDBox width="140px">
                            <TextField
                                placeholder="Search..."
                                size="small"
                                fullWidth
                                value={invitationSearch}
                                onChange={(e) => setInvitationSearch(e.target.value)}
                                InputProps={{ sx: { fontSize: "0.8rem" } }}
                            />
                        </MDBox>
                    </MDBox>

                    {loadingInvitations ? (
                        <MDBox display="flex" justifyContent="center" py={2}>
                            <CircularProgress size={20} />
                        </MDBox>
                    ) : filteredInvitations.length === 0 ? (
                        <MDTypography variant="caption" color="text" sx={{ fontStyle: "italic" }}>
                            No pending invitations.
                        </MDTypography>
                    ) : (
                        filteredInvitations.map((inv) => (
                            <MDBox
                                key={inv._id}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                p={1.5}
                                mb={1}
                                bgcolor="white"
                                borderRadius="8px"
                                sx={{ border: "1px solid #eee" }}
                            >
                                <MDTypography variant="caption" color="text">
                                    {inv.email}
                                </MDTypography>
                                <Chip label={inv.role || "Viewer"} size="small" sx={{ fontSize: "0.7rem" }} />
                            </MDBox>
                        ))
                    )}
                </MDBox>
            </DialogContent>

            {/* Footer actions */}
            <DialogActions sx={{ px: 3, pb: 2, pt: 0, gap: 1 }}>
                {isOnboarding ? (
                    <>
                        <MDButton variant="text" color="secondary" onClick={onClose}>
                            Skip for now
                        </MDButton>
                        <MDButton variant="contained" color="info" onClick={onClose}>
                            Done
                        </MDButton>
                    </>
                ) : (
                    <MDButton variant="contained" color="info" onClick={onClose}>
                        Close
                    </MDButton>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default ManageTeamModal;
